// src/screens/DeveloperPortalScreen.js
// ============================================================================
// DEVELOPER PORTAL
//
// POST-PIVOT (2026-06-27): the 3rd-party real-money COMPETITION INTEGRATION
// path (API match/tournament integration + cash revenue share from prize-pool
// platform fees) is PAUSED/ABANDONED IN PLACE. Under the Crit model devs would
// only earn Crits from competitions, and devs want hard cash — so that pitch is
// off the table for now. The portal is reframed to the ONE thing we still offer
// developers: SELL YOUR GAME on CritCash (90% game-sales revenue share).
//
// All competition-integration sections (integration levels, API docs, custodial
// model, competition revenue tiers) are gated behind SHOW_COMPETITION_INTEGRATION
// and kept in code (not deleted) so the path can be revived later.
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, ScrollView,
  TouchableOpacity, ActivityIndicator, Linking, Modal, TextInput
} from 'react-native';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../../firebaseConfig';

// Icon imports - using @expo/vector-icons (built into Expo)
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useError } from '../components/ErrorContext';

// Master switch for the paused 3rd-party competition-integration pitch (API
// match/tournament integration + cash competition revenue share). false =
// portal shows only "sell your game" (the path we still offer post-pivot).
// Flip to true to revive the full integration partnership program.
const SHOW_COMPETITION_INTEGRATION = false;

// ============================================================================
// INTEGRATION LEVELS - Accurate representation of what we offer
// ============================================================================
const INTEGRATION_LEVELS = [
  {
    level: 0,
    name: 'Stream-Based',
    description: 'Zero code required. Match results are detected automatically from player streams.',
    features: [
      'No SDK integration needed',
      'Automatic result detection via stream analysis',
      'Works with any game that can be streamed',
      'Fastest time to market',
    ],
    ideal: 'Indie games, legacy titles, games without dedicated servers',
    iconName: 'eye',
    iconType: 'Ionicons',
  },
  {
    level: 1,
    name: 'Result Reporting',
    description: 'Your game server reports match results directly to our API. Simple webhook-style integration for authoritative results.',
    features: [
      'POST /api/v1/match/result endpoint',
      'HMAC-signed request verification',
      'Real-time fund distribution processing',
      'Match history and statistics API',
    ],
    ideal: 'Games with authoritative servers, competitive multiplayer titles',
    iconName: 'cloud-upload',
    iconType: 'Ionicons',
  },
  {
    level: 2,
    name: 'Event Management',
    description: 'Create and manage tournaments directly from your game. Players enter through your UI, we handle the funds.',
    features: [
      'Tournament creation API',
      'Entry fee collection and custody',
      'Bracket generation and management',
      'Player-funded pool distribution',
    ],
    ideal: 'Competitive multiplayer games, esports-focused titles',
    iconName: 'trophy',
    iconType: 'Ionicons',
  },
  {
    level: 3,
    name: 'Full Integration',
    description: 'Complete in-game wallet and competition system. The ultimate competitive gaming experience.',
    features: [
      'In-game wallet display',
      'Native deposit/withdraw flows',
      'Deep tournament integration',
      'Custom branding options',
    ],
    ideal: 'AAA competitive games, platform-exclusive titles',
    iconName: 'medal',
    iconType: 'Ionicons',
  },
];

// ============================================================================
// REVENUE SHARE - Game Sales (Undercut Steam/Epic)
// Steam: 30% (25% after $10M, 20% after $50M)
// Epic: 12% flat
// CritCash: 10% flat - Industry leading
// ============================================================================
const GAME_SALES_REVENUE = {
  devShare: '90%',
  platformShare: '10%',
  comparison: [
    { platform: 'Steam', devShare: '70%', platformShare: '30%' },
    { platform: 'Epic Games Store', devShare: '88%', platformShare: '12%' },
    { platform: 'CritCash', devShare: '90%', platformShare: '10%', highlight: true },
  ],
};

// ============================================================================
// PLATFORM FEE SHARE - Competition Integration
// Platform fee = 10% of entry fees
// Host rewards = 29% of platform fee (fixed: 4% base + 25% bonus)
// Remaining 71% split between developer and CritCash
// ============================================================================
const COMPETITION_REVENUE_TIERS = [
  { volume: '$0 - $50K/mo', devShare: '10%', hostShare: '29%', platformShare: '61%', note: 'Starter tier' },
  { volume: '$50K - $250K/mo', devShare: '15%', hostShare: '29%', platformShare: '56%', note: 'Growth tier' },
  { volume: '$250K - $1M/mo', devShare: '18%', hostShare: '29%', platformShare: '53%', note: 'Partner tier' },
  { volume: '$1M+/mo', devShare: '20%', hostShare: '29%', platformShare: '51%', note: 'Premier partner' },
];

// Helper to render icons
const getIcon = (name, type, size = 32, color = '#FCD000') => {
  if (type === 'Ionicons') {
    return <Ionicons name={name} size={size} color={color} />;
  } else if (type === 'MaterialCommunityIcons') {
    return <MaterialCommunityIcons name={name} size={size} color={color} />;
  } else if (type === 'FontAwesome5') {
    return <FontAwesome5 name={name} size={size} color={color} />;
  }
  return <Ionicons name="help-circle" size={size} color={color} />;
};

export default function DeveloperPortalScreen({ navigation }) {
  const { showError } = useError();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);
  
  const [hasDeveloperAccount, setHasDeveloperAccount] = useState(false);

  // Application form state
  const [formData, setFormData] = useState({
    gameName: '',
    studioName: '',
    email: '',
    website: '',
    description: '',
    playerCount: '',
    teamSize: '',
  });

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        // Check if user already has a developer account
        try {
          const { getFirestore, collection, query, where, getDocs } = require('firebase/firestore');
          const firestore = getFirestore();
          const q = query(
            collection(firestore, 'developers'),
            where('memberUids', 'array-contains', u.uid)
          );
          const snap = await getDocs(q);
          setHasDeveloperAccount(!snap.empty);
        } catch (err) {
          console.log('Developer account check failed:', err.message);
        }
      }
      setLoading(false);
    });
    return unsubAuth;
  }, []);

  const handleApply = (level) => {
    setSelectedLevel(level);
    setShowApplyModal(true);
  };

  const handleSubmitApplication = async () => {
    if (!formData.gameName || !formData.studioName || !formData.email) {
      showError('Missing Information', 'Please fill in all required fields.');
      return;
    }

    setSubmitting(true);
    try {
      const functions = getFunctions();
      const submitDevApplication = httpsCallable(functions, 'submitDeveloperApplication');
      await submitDevApplication({
        ...formData,
        integrationLevel: selectedLevel?.level,
        userId: user?.uid || null,
      });

      setShowApplyModal(false);
      setFormData({
        gameName: '',
        studioName: '',
        email: '',
        website: '',
        description: '',
        playerCount: '',
        teamSize: '',
      });
      
      showError('Application Submitted!', 'Our partnerships team will review your application and reach out within 2-3 business days.');
    } catch (error) {
      console.error('Error submitting application:', error);
      showError('Submission Error', 'Failed to submit application. Please try again or contact partnerships@critcash.com');
    } finally {
      setSubmitting(false);
    }
  };

  const openDocs = () => {
    Linking.openURL('https://critcash.com/api.info');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <Header navigation={navigation} />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#FCD000" />
          <Text style={styles.loadingText}>Loading Developer Portal...</Text>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <Header navigation={navigation} />
      
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Hero Section */}
        <View style={styles.heroSection}>
          <View style={styles.heroIconContainer}>
            <Ionicons name="code-slash" size={48} color="#FCD000" />
          </View>
          <Text style={styles.heroTitle}>Developer Portal</Text>
          <Text style={styles.heroSubtitle}>
            Sell your game on CritCash and keep 90% of every sale — industry-leading
            revenue share, no thresholds, no hidden fees.
          </Text>

          <View style={styles.heroCTA}>
            <TouchableOpacity
              style={styles.primaryButton}
              onPress={() => handleApply(null)}
            >
              <Text style={styles.primaryButtonText}>Submit Your Game</Text>
            </TouchableOpacity>
            {SHOW_COMPETITION_INTEGRATION && (
              <TouchableOpacity
                style={styles.secondaryButton}
                onPress={openDocs}
              >
                <Text style={styles.secondaryButtonText}>View API Docs</Text>
              </TouchableOpacity>
            )}
          </View>
          
          {user && hasDeveloperAccount ? (
            <View style={{ backgroundColor: '#FCD00015', borderRadius: 12, padding: 16, marginTop: 16, alignItems: 'center', borderWidth: 1, borderColor: '#FCD00033' }}>
              <Text style={{ color: '#FCD000', fontWeight: '700', fontSize: 15, marginBottom: 6 }}>You already have a developer account</Text>
              <TouchableOpacity
                style={[styles.primaryButton, { marginTop: 4 }]}
                onPress={() => navigation.navigate('DeveloperDashboard')}
              >
                <Text style={styles.primaryButtonText}>Go to Dashboard</Text>
              </TouchableOpacity>
            </View>
          ) : user ? (
            <TouchableOpacity
              style={styles.dashboardLoginButton}
              onPress={() => navigation.navigate('DeveloperDashboard')}
            >
              <Ionicons name="log-in-outline" size={18} color="#FCD000" />
              <Text style={styles.dashboardLoginText}>Developer Dashboard</Text>
            </TouchableOpacity>
          ) : null}
        </View>

        {SHOW_COMPETITION_INTEGRATION && (<>
        {/* Value Proposition - No fake stats */}
        <View style={styles.valueSection}>
          <View style={styles.valueItem}>
            <Ionicons name="shield-checkmark" size={28} color="#2ECC71" />
            <Text style={styles.valueTitle}>Fully Compliant</Text>
            <Text style={styles.valueText}>
              State-by-state licensing, age verification, and responsible gaming built in.
            </Text>
          </View>
          <View style={styles.valueDivider} />
          <View style={styles.valueItem}>
            <Ionicons name="wallet" size={28} color="#3498db" />
            <Text style={styles.valueTitle}>Custodial Model</Text>
            <Text style={styles.valueText}>
              We hold player funds in a custodial account and distribute based on results.
            </Text>
          </View>
          <View style={styles.valueDivider} />
          <View style={styles.valueItem}>
            <Ionicons name="flash" size={28} color="#FCD000" />
            <Text style={styles.valueTitle}>Instant Distribution</Text>
            <Text style={styles.valueText}>
              Funds released to winners within seconds of match completion.
            </Text>
          </View>
        </View>

        {/* Integration Levels */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Integration Levels</Text>
          <Text style={styles.sectionSubtitle}>
            Choose the integration depth that fits your game and resources
          </Text>

          {INTEGRATION_LEVELS.map((level) => (
            <View key={level.level} style={styles.levelCard}>
              <View style={styles.levelHeader}>
                <View style={styles.levelBadge}>
                  {getIcon(level.iconName, level.iconType, 24, '#FCD000')}
                  <Text style={styles.levelNumber}>Level {level.level}</Text>
                </View>
                <Text style={styles.levelName}>{level.name}</Text>
              </View>
              
              <Text style={styles.levelDescription}>{level.description}</Text>
              
              <View style={styles.featureList}>
                {level.features.map((feature, idx) => (
                  <View key={idx} style={styles.featureItem}>
                    <Ionicons name="checkmark" size={16} color="#2ECC71" />
                    <Text style={styles.featureText}>{feature}</Text>
                  </View>
                ))}
              </View>
              
              <View style={styles.idealFor}>
                <Text style={styles.idealLabel}>Ideal for:</Text>
                <Text style={styles.idealText}>{level.ideal}</Text>
              </View>
              
              <TouchableOpacity 
                style={styles.levelButton}
                onPress={() => handleApply(level)}
              >
                <Text style={styles.levelButtonText}>Apply for Level {level.level}</Text>
              </TouchableOpacity>
            </View>
          ))}
        </View>
        </>)}

        {/* Game Sales Revenue */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Game Sales Revenue</Text>
          <Text style={styles.sectionSubtitle}>
            Sell your game on CritCash with industry-leading revenue share
          </Text>

          <View style={styles.revenueCard}>
            <View style={styles.revenueHighlightBox}>
              <Text style={styles.revenueHighlightLabel}>You Keep</Text>
              <Text style={styles.revenueHighlightValue}>90%</Text>
              <Text style={styles.revenueHighlightNote}>of every game sale</Text>
            </View>
            
            <Text style={styles.comparisonTitle}>Platform Comparison</Text>
            <View style={styles.comparisonTable}>
              <View style={styles.comparisonHeader}>
                <Text style={[styles.comparisonHeaderText, { flex: 2 }]}>Platform</Text>
                <Text style={[styles.comparisonHeaderText, { flex: 1 }]}>Dev Share</Text>
                <Text style={[styles.comparisonHeaderText, { flex: 1 }]}>Platform</Text>
              </View>
              
              {GAME_SALES_REVENUE.comparison.map((item, idx) => (
                <View 
                  key={idx} 
                  style={[
                    styles.comparisonRow, 
                    item.highlight && styles.comparisonRowHighlight,
                    idx === GAME_SALES_REVENUE.comparison.length - 1 && styles.comparisonRowLast
                  ]}
                >
                  <Text style={[styles.comparisonText, { flex: 2 }, item.highlight && styles.comparisonTextHighlight]}>
                    {item.platform}
                  </Text>
                  <Text style={[styles.comparisonValue, { flex: 1 }, item.highlight && styles.comparisonValueHighlight]}>
                    {item.devShare}
                  </Text>
                  <Text style={[styles.comparisonText, { flex: 1 }]}>
                    {item.platformShare}
                  </Text>
                </View>
              ))}
            </View>
            
            <Text style={styles.revenueNote}>
              No hidden fees. No revenue thresholds. 90% from day one.
            </Text>
          </View>
        </View>

        {SHOW_COMPETITION_INTEGRATION && (<>
        {/* Competition Revenue Share */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Competition Integration Revenue</Text>
          <Text style={styles.sectionSubtitle}>
            Earn a share of the platform fee when players compete in your game
          </Text>

          <View style={styles.revenueCard}>
            <View style={styles.revenueExplainer}>
              <Ionicons name="information-circle" size={20} color="#3498db" />
              <Text style={styles.revenueExplainerText}>
                CritCash collects a platform fee (10%) from player-funded prize pools.
                This fee is split three ways: Host rewards (29% fixed), Developer share (tiered), and CritCash.
              </Text>
            </View>
            
            <View style={styles.revenueHeader}>
              <Text style={[styles.revenueHeaderText, { flex: 2 }]}>Monthly Volume</Text>
              <Text style={[styles.revenueHeaderText, { flex: 1 }]}>You</Text>
              <Text style={[styles.revenueHeaderText, { flex: 1 }]}>Hosts</Text>
              <Text style={[styles.revenueHeaderText, { flex: 1 }]}>Platform</Text>
            </View>
            
            {COMPETITION_REVENUE_TIERS.map((tier, idx) => (
              <View 
                key={idx} 
                style={[styles.revenueRow, idx === COMPETITION_REVENUE_TIERS.length - 1 && styles.revenueRowLast]}
              >
                <View style={{ flex: 2 }}>
                  <Text style={styles.revenueText}>{tier.volume}</Text>
                  <Text style={styles.revenueTierNote}>{tier.note}</Text>
                </View>
                <Text style={[styles.revenueHighlight, { flex: 1 }]}>{tier.devShare}</Text>
                <Text style={[styles.revenueText, { flex: 1, color: '#9146FF' }]}>{tier.hostShare}</Text>
                <Text style={[styles.revenueText, { flex: 1 }]}>{tier.platformShare}</Text>
              </View>
            ))}
            
            <View style={styles.revenueExample}>
              <Text style={styles.revenueExampleTitle}>Example Calculation (Growth Tier)</Text>
              <Text style={styles.revenueExampleText}>
                Your game generates $100,000/month in competition entry fees{'\n'}
                Platform fee (10%): $10,000{'\n'}
                {'\n'}
                Host rewards (29%): $2,900{'\n'}
                Your share (15%): <Text style={{ color: '#FCD000', fontWeight: 'bold' }}>$1,500/month</Text>{'\n'}
                CritCash (56%): $5,600
              </Text>
            </View>

            <View style={styles.hostRewardNote}>
              <Ionicons name="people" size={16} color="#9146FF" />
              <Text style={styles.hostRewardNoteText}>
                Host rewards go to players who facilitate matches. This incentivizes reliable hosting 
                and improves match quality for everyone. Learn more in our Host Program.
              </Text>
            </View>
          </View>
        </View>

        {/* What We Provide */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>What CritCash Provides</Text>
          
          <View style={styles.providesGrid}>
            <View style={styles.providesCard}>
              <Ionicons name="shield-checkmark" size={28} color="#2ECC71" />
              <Text style={styles.providesTitle}>Compliance Infrastructure</Text>
              <Text style={styles.providesText}>
                State-by-state licensing, age verification (18+), KYC/AML checks, 
                and responsible gaming controls. We handle the legal complexity.
              </Text>
            </View>
            
            <View style={styles.providesCard}>
              <Ionicons name="wallet" size={28} color="#3498db" />
              <Text style={styles.providesTitle}>Fund Custody</Text>
              <Text style={styles.providesText}>
                Entry fees held in a custodial account. Player-funded prize pools distributed
                automatically based on competition outcomes. Instant fund releases.
              </Text>
            </View>
            
            <View style={styles.providesCard}>
              <Ionicons name="eye" size={28} color="#9146FF" />
              <Text style={styles.providesTitle}>Stream-Based Verification</Text>
              <Text style={styles.providesText}>
                Match results detected automatically from player streams.
                No integration required for stream-based games.
              </Text>
            </View>
            
            <View style={styles.providesCard}>
              <Ionicons name="git-branch" size={28} color="#FCD000" />
              <Text style={styles.providesTitle}>Tournament Infrastructure</Text>
              <Text style={styles.providesText}>
                Bracket generation, round progression, host assignment, 
                and automated fund distribution upon completion.
              </Text>
            </View>
          </View>
        </View>

        {/* What We Don't Do (Transparency) */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Current Limitations</Text>
          <Text style={styles.sectionSubtitle}>
            Transparency about what we don't currently offer
          </Text>
          
          <View style={styles.limitationsCard}>
            <View style={styles.limitationItem}>
              <Ionicons name="close-circle" size={20} color="#e74c3c" />
              <View style={styles.limitationContent}>
                <Text style={styles.limitationTitle}>Skill-Based Matchmaking</Text>
                <Text style={styles.limitationText}>
                  Tournament seeding is randomized to prevent collusion. We don't 
                  currently offer ELO/MMR-based matching. Bracket positions are 
                  assigned randomly with anti-fraud measures to prevent repeated pairings.
                </Text>
              </View>
            </View>
            
            <View style={styles.limitationItem}>
              <Ionicons name="close-circle" size={20} color="#e74c3c" />
              <View style={styles.limitationContent}>
                <Text style={styles.limitationTitle}>In-Game Anti-Cheat</Text>
                <Text style={styles.limitationText}>
                  We don't provide kernel-level anti-cheat software. Cheating reports 
                  are handled manually through our dispute system. We recommend 
                  integrating your own anti-cheat solution for game integrity.
                </Text>
              </View>
            </View>
            
            <View style={styles.limitationItem}>
              <Ionicons name="time" size={20} color="#f39c12" />
              <View style={styles.limitationContent}>
                <Text style={styles.limitationTitle}>Coming Soon: Enhanced Fraud Detection</Text>
                <Text style={styles.limitationText}>
                  We currently detect gift card fraud and collusion patterns. 
                  Advanced anti-cheat integrations and behavioral analysis are 
                  on our roadmap for future releases.
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* API Preview */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Simple API</Text>
          <Text style={styles.sectionSubtitle}>
            Get started with just a few lines of code
          </Text>
          
          <View style={styles.codeBlock}>
            <Text style={styles.codeComment}>// Report match result (Level 1+)</Text>
            <Text style={styles.codeLine}>
              <Text style={styles.codeKeyword}>POST</Text> /api/v1/match/result
            </Text>
            <Text style={styles.codeJson}>{`{
  "matchId": "match_abc123",
  "winner": "player_xyz",
  "scores": { "player_xyz": 3, "player_abc": 1 },
  "duration": 847,
  "signature": "hmac_sha256_signature"
}`}</Text>
          </View>

          <View style={styles.codeBlock}>
            <Text style={styles.codeComment}>// Create tournament (Level 2+)</Text>
            <Text style={styles.codeLine}>
              <Text style={styles.codeKeyword}>POST</Text> /api/v1/tournament/create
            </Text>
            <Text style={styles.codeJson}>{`{
  "gameId": "your_game_id",
  "name": "Weekend Championship",
  "entryFeeCents": 500,
  "maxPlayers": 32,
  "format": "single_elimination",
  "startsAt": "2025-02-01T18:00:00Z"
}`}</Text>
          </View>
          
          <TouchableOpacity style={styles.docsButton} onPress={openDocs}>
            <Ionicons name="document-text" size={18} color="#FFF" style={{ marginRight: 8 }} />
            <Text style={styles.docsButtonText}>Full API Documentation</Text>
          </TouchableOpacity>
        </View>

        {/* Custodial Model Explanation */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Our Custodial Model</Text>
          <Text style={styles.sectionSubtitle}>
            Understanding how funds flow through CritCash
          </Text>
          
          <View style={styles.custodialCard}>
            <View style={styles.custodialStep}>
              <View style={styles.custodialStepNumber}>
                <Text style={styles.custodialStepNumberText}>1</Text>
              </View>
              <View style={styles.custodialStepContent}>
                <Text style={styles.custodialStepTitle}>Players Enter Competition</Text>
                <Text style={styles.custodialStepText}>
                  Entry fees are collected and held in a custodial account by CritCash.
                </Text>
              </View>
            </View>
            
            <View style={styles.custodialArrow}>
              <Ionicons name="arrow-down" size={20} color="#666" />
            </View>
            
            <View style={styles.custodialStep}>
              <View style={styles.custodialStepNumber}>
                <Text style={styles.custodialStepNumberText}>2</Text>
              </View>
              <View style={styles.custodialStepContent}>
                <Text style={styles.custodialStepTitle}>Prize Pool Formed</Text>
                <Text style={styles.custodialStepText}>
                  100% of entry fees minus platform fee form the player-funded prize pool.
                </Text>
              </View>
            </View>
            
            <View style={styles.custodialArrow}>
              <Ionicons name="arrow-down" size={20} color="#666" />
            </View>
            
            <View style={styles.custodialStep}>
              <View style={styles.custodialStepNumber}>
                <Text style={styles.custodialStepNumberText}>3</Text>
              </View>
              <View style={styles.custodialStepContent}>
                <Text style={styles.custodialStepTitle}>Competition Runs</Text>
                <Text style={styles.custodialStepText}>
                  Results verified via vision system or API reporting.
                </Text>
              </View>
            </View>
            
            <View style={styles.custodialArrow}>
              <Ionicons name="arrow-down" size={20} color="#666" />
            </View>
            
            <View style={styles.custodialStep}>
              <View style={styles.custodialStepNumber}>
                <Text style={styles.custodialStepNumberText}>4</Text>
              </View>
              <View style={styles.custodialStepContent}>
                <Text style={styles.custodialStepTitle}>Funds Distributed</Text>
                <Text style={styles.custodialStepText}>
                  CritCash releases funds to winners based on placement. We facilitate, not sponsor.
                </Text>
              </View>
            </View>
            
            <View style={styles.custodialDisclaimer}>
              <Ionicons name="information-circle" size={16} color="#666" />
              <Text style={styles.custodialDisclaimerText}>
                CritCash acts as custodian of player funds. Prize pools are funded entirely 
                by participant entry fees. CritCash does not contribute to or sponsor prize pools.
              </Text>
            </View>
          </View>
        </View>

        </>)}

        {/* Contact Section */}
        <View style={styles.contactSection}>
          <Text style={styles.contactTitle}>Ready to Get Started?</Text>
          <Text style={styles.contactText}>
            Our partnerships team is here to help you bring your game to CritCash.
            Submit your game above and we'll be in touch within 2-3 business days.
          </Text>

          <TouchableOpacity
            style={styles.contactButton}
            onPress={() => Linking.openURL('mailto:partnerships@critcash.com')}
          >
            <Text style={styles.contactButtonText}>Contact Partnerships Team</Text>
          </TouchableOpacity>
          
          <Text style={styles.contactEmail}>partnerships@critcash.com</Text>
        </View>

        <View style={styles.bottomPadding} />
      </ScrollView>

      {/* Application Modal */}
      <Modal
        visible={showApplyModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowApplyModal(false)}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalContainer}>
            <ScrollView style={styles.modalScroll} contentContainerStyle={styles.modalScrollContent}>
              <View style={styles.modalHeader}>
                {selectedLevel
                  ? getIcon(selectedLevel.iconName, selectedLevel.iconType, 48, '#FCD000')
                  : <Ionicons name="game-controller" size={48} color="#FCD000" />}
                <Text style={styles.modalTitle}>
                  {selectedLevel ? 'Developer Application' : 'Submit Your Game'}
                </Text>
                <Text style={styles.modalSubtitle}>
                  {selectedLevel
                    ? `Level ${selectedLevel.level}: ${selectedLevel.name}`
                    : 'Sell your game on CritCash — 90% revenue share'}
                </Text>
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Game Name *</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="Enter your game's name"
                  placeholderTextColor="#666"
                  value={formData.gameName}
                  onChangeText={(text) => setFormData({ ...formData, gameName: text })}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Studio / Company Name *</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="Your studio or company name"
                  placeholderTextColor="#666"
                  value={formData.studioName}
                  onChangeText={(text) => setFormData({ ...formData, studioName: text })}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Email Address *</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="business@yourstudio.com"
                  placeholderTextColor="#666"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  value={formData.email}
                  onChangeText={(text) => setFormData({ ...formData, email: text })}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Website (Optional)</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="https://yourgame.com"
                  placeholderTextColor="#666"
                  autoCapitalize="none"
                  value={formData.website}
                  onChangeText={(text) => setFormData({ ...formData, website: text })}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Monthly Active Players (Estimate)</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="e.g., 10,000"
                  placeholderTextColor="#666"
                  keyboardType="numeric"
                  value={formData.playerCount}
                  onChangeText={(text) => setFormData({ ...formData, playerCount: text })}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Team Size</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="e.g., 1, 2-5, 10+"
                  placeholderTextColor="#666"
                  value={formData.teamSize}
                  onChangeText={(text) => setFormData({ ...formData, teamSize: text })}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Tell Us About Your Game</Text>
                <TextInput
                  style={[styles.formInput, styles.formTextArea]}
                  placeholder="Genre, platform, competitive features, current player base, etc."
                  placeholderTextColor="#666"
                  multiline
                  numberOfLines={4}
                  value={formData.description}
                  onChangeText={(text) => setFormData({ ...formData, description: text })}
                />
              </View>

              <View style={styles.modalActions}>
                <TouchableOpacity
                  style={styles.modalButtonCancel}
                  onPress={() => setShowApplyModal(false)}
                >
                  <Text style={styles.modalButtonCancelText}>Cancel</Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[styles.modalButtonSubmit, submitting && styles.modalButtonDisabled]}
                  onPress={handleSubmitApplication}
                  disabled={submitting}
                >
                  {submitting ? (
                    <ActivityIndicator size="small" color="#000" />
                  ) : (
                    <Text style={styles.modalButtonSubmitText}>Submit Application</Text>
                  )}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#999',
    marginTop: 12,
    fontSize: 14,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 80,
  },
  
  // Hero Section
  heroSection: {
    padding: 24,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
  },
  heroIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#1A1A1A',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
    borderWidth: 2,
    borderColor: '#FCD000',
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 12,
  },
  heroSubtitle: {
    fontSize: 15,
    color: '#999',
    textAlign: 'center',
    maxWidth: 400,
    lineHeight: 22,
    marginBottom: 24,
  },
  heroCTA: {
    flexDirection: 'row',
    gap: 12,
  },
  primaryButton: {
    backgroundColor: '#FCD000',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
  primaryButtonText: {
    color: '#000',
    fontSize: 15,
    fontWeight: 'bold',
  },
  secondaryButton: {
    backgroundColor: '#1A1A1A',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#333',
  },
  secondaryButtonText: {
    color: '#FFF',
    fontSize: 15,
    fontWeight: '600',
  },
  dashboardLoginButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 16,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FCD00044',
    backgroundColor: '#FCD00011',
  },
  dashboardLoginText: {
    color: '#FCD000',
    fontSize: 14,
    fontWeight: '600',
  },
  
  // Value Section
  valueSection: {
    flexDirection: 'row',
    backgroundColor: '#0A0A0A',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
  },
  valueItem: {
    flex: 1,
    alignItems: 'center',
  },
  valueTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#FFF',
    marginTop: 8,
    marginBottom: 4,
    textAlign: 'center',
  },
  valueText: {
    fontSize: 11,
    color: '#888',
    textAlign: 'center',
    lineHeight: 16,
  },
  valueDivider: {
    width: 1,
    backgroundColor: '#222',
    marginHorizontal: 12,
  },
  
  // Sections
  section: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
  },
  sectionTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 8,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#888',
    marginBottom: 20,
    lineHeight: 20,
  },
  
  // Level Cards
  levelCard: {
    backgroundColor: '#111',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#222',
  },
  levelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  levelBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1A1A1A',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 20,
    marginRight: 12,
    gap: 6,
  },
  levelNumber: {
    fontSize: 12,
    color: '#FCD000',
    fontWeight: 'bold',
  },
  levelName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFF',
    flex: 1,
  },
  levelDescription: {
    fontSize: 14,
    color: '#AAA',
    lineHeight: 20,
    marginBottom: 16,
  },
  featureList: {
    marginBottom: 16,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
    gap: 8,
  },
  featureText: {
    fontSize: 13,
    color: '#CCC',
    flex: 1,
    lineHeight: 18,
  },
  idealFor: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  idealLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#FCD000',
    marginRight: 8,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  idealText: {
    fontSize: 13,
    color: '#999',
    flex: 1,
  },
  levelButton: {
    backgroundColor: '#1A1A1A',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#FCD000',
  },
  levelButtonText: {
    color: '#FCD000',
    fontSize: 14,
    fontWeight: 'bold',
  },
  
  // Revenue Cards
  revenueCard: {
    backgroundColor: '#111',
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#222',
  },
  revenueHighlightBox: {
    backgroundColor: '#1A2A1A',
    padding: 20,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#2ECC7130',
  },
  revenueHighlightLabel: {
    fontSize: 12,
    color: '#2ECC71',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 4,
  },
  revenueHighlightValue: {
    fontSize: 48,
    fontWeight: 'bold',
    color: '#2ECC71',
  },
  revenueHighlightNote: {
    fontSize: 14,
    color: '#888',
  },
  comparisonTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#888',
    padding: 16,
    paddingBottom: 8,
    textTransform: 'uppercase',
  },
  comparisonTable: {
    paddingHorizontal: 16,
  },
  comparisonHeader: {
    flexDirection: 'row',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  comparisonHeaderText: {
    fontSize: 11,
    color: '#666',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  comparisonRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
  },
  comparisonRowHighlight: {
    backgroundColor: '#1A2A1A',
    marginHorizontal: -16,
    paddingHorizontal: 16,
  },
  comparisonRowLast: {
    borderBottomWidth: 0,
  },
  comparisonText: {
    fontSize: 14,
    color: '#999',
  },
  comparisonTextHighlight: {
    color: '#FFF',
    fontWeight: 'bold',
  },
  comparisonValue: {
    fontSize: 14,
    color: '#CCC',
    fontWeight: '600',
  },
  comparisonValueHighlight: {
    color: '#2ECC71',
    fontWeight: 'bold',
  },
  revenueNote: {
    fontSize: 12,
    color: '#888',
    padding: 16,
    backgroundColor: '#0A0A0A',
    textAlign: 'center',
  },
  revenueExplainer: {
    flexDirection: 'row',
    padding: 16,
    backgroundColor: '#0A1520',
    gap: 12,
    alignItems: 'flex-start',
  },
  revenueExplainerText: {
    flex: 1,
    fontSize: 13,
    color: '#8AB4D8',
    lineHeight: 18,
  },
  revenueHeader: {
    flexDirection: 'row',
    backgroundColor: '#1A1A1A',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  revenueHeaderText: {
    fontSize: 11,
    color: '#888',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  revenueRow: {
    flexDirection: 'row',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
    alignItems: 'center',
  },
  revenueRowLast: {
    borderBottomWidth: 0,
  },
  revenueText: {
    fontSize: 14,
    color: '#CCC',
  },
  revenueTierNote: {
    fontSize: 11,
    color: '#666',
    marginTop: 2,
  },
  revenueHighlight: {
    fontSize: 14,
    color: '#FCD000',
    fontWeight: 'bold',
  },
  revenueExample: {
    padding: 16,
    backgroundColor: '#0A0A0A',
  },
  revenueExampleTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#888',
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  revenueExampleText: {
    fontSize: 13,
    color: '#AAA',
    lineHeight: 20,
  },
  hostRewardNote: {
    flexDirection: 'row',
    padding: 12,
    backgroundColor: '#1A1A2A',
    gap: 10,
    alignItems: 'flex-start',
    borderTopWidth: 1,
    borderTopColor: '#9146FF30',
  },
  hostRewardNoteText: {
    flex: 1,
    fontSize: 12,
    color: '#9D8EC7',
    lineHeight: 18,
  },
  
  // Provides Grid
  providesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -8,
  },
  providesCard: {
    width: '50%',
    padding: 8,
  },
  providesCardInner: {
    backgroundColor: '#111',
    borderRadius: 12,
    padding: 16,
    minHeight: 140,
    borderWidth: 1,
    borderColor: '#222',
  },
  providesTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#FFF',
    marginTop: 12,
    marginBottom: 8,
  },
  providesText: {
    fontSize: 12,
    color: '#888',
    lineHeight: 18,
  },
  
  // Limitations
  limitationsCard: {
    backgroundColor: '#111',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#222',
    overflow: 'hidden',
  },
  limitationItem: {
    flexDirection: 'row',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
    gap: 12,
  },
  limitationContent: {
    flex: 1,
  },
  limitationTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 4,
  },
  limitationText: {
    fontSize: 13,
    color: '#888',
    lineHeight: 18,
  },
  
  // Code Block
  codeBlock: {
    backgroundColor: '#0A0A0A',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#1A1A1A',
  },
  codeComment: {
    fontSize: 12,
    color: '#666',
    fontFamily: 'monospace',
    marginBottom: 8,
  },
  codeLine: {
    fontSize: 13,
    color: '#FFF',
    fontFamily: 'monospace',
    marginBottom: 8,
  },
  codeKeyword: {
    color: '#FCD000',
    fontWeight: 'bold',
  },
  codeJson: {
    fontSize: 12,
    color: '#4CAF50',
    fontFamily: 'monospace',
    lineHeight: 18,
  },
  docsButton: {
    backgroundColor: '#1A1A1A',
    borderRadius: 8,
    padding: 14,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#333',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  docsButtonText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '600',
  },
  
  // Custodial Model
  custodialCard: {
    backgroundColor: '#111',
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: '#222',
  },
  custodialStep: {
    flexDirection: 'row',
    gap: 12,
  },
  custodialStepNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#FCD000',
    justifyContent: 'center',
    alignItems: 'center',
  },
  custodialStepNumberText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000',
  },
  custodialStepContent: {
    flex: 1,
  },
  custodialStepTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 4,
  },
  custodialStepText: {
    fontSize: 13,
    color: '#888',
    lineHeight: 18,
  },
  custodialArrow: {
    alignItems: 'center',
    paddingVertical: 8,
    paddingLeft: 6,
  },
  custodialDisclaimer: {
    flexDirection: 'row',
    marginTop: 20,
    padding: 12,
    backgroundColor: '#0A0A0A',
    borderRadius: 8,
    gap: 10,
  },
  custodialDisclaimerText: {
    flex: 1,
    fontSize: 11,
    color: '#666',
    lineHeight: 16,
  },
  
  // Contact Section
  contactSection: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 20,
  },
  contactTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 12,
    textAlign: 'center',
  },
  contactText: {
    fontSize: 14,
    color: '#888',
    textAlign: 'center',
    maxWidth: 400,
    marginBottom: 24,
    lineHeight: 22,
  },
  contactButton: {
    backgroundColor: '#FCD000',
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 8,
    marginBottom: 12,
  },
  contactButtonText: {
    color: '#000',
    fontSize: 16,
    fontWeight: 'bold',
  },
  contactEmail: {
    fontSize: 14,
    color: '#666',
  },
  
  bottomPadding: {
    height: 40,
  },
  
  // Modal Styles
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContainer: {
    backgroundColor: '#111',
    borderRadius: 16,
    width: '100%',
    maxWidth: 500,
    maxHeight: '90%',
    overflow: 'hidden',
  },
  modalScroll: {
    maxHeight: '100%',
  },
  modalScrollContent: {
    padding: 24,
  },
  modalHeader: {
    alignItems: 'center',
    marginBottom: 24,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFF',
    marginTop: 12,
    marginBottom: 4,
  },
  modalSubtitle: {
    fontSize: 14,
    color: '#FCD000',
  },
  formGroup: {
    marginBottom: 16,
  },
  formLabel: {
    fontSize: 13,
    color: '#999',
    marginBottom: 8,
    fontWeight: '600',
  },
  formInput: {
    backgroundColor: '#1A1A1A',
    borderRadius: 8,
    padding: 14,
    fontSize: 15,
    color: '#FFF',
    borderWidth: 1,
    borderColor: '#333',
  },
  formTextArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  modalButtonCancel: {
    flex: 1,
    backgroundColor: '#1A1A1A',
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalButtonCancelText: {
    color: '#FFF',
    fontSize: 15,
    fontWeight: '600',
  },
  modalButtonSubmit: {
    flex: 2,
    backgroundColor: '#FCD000',
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalButtonSubmitText: {
    color: '#000',
    fontSize: 15,
    fontWeight: 'bold',
  },
  modalButtonDisabled: {
    backgroundColor: '#666',
  },
});